import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Search, FileText, Clock, Calendar, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';
import RichTextEditor from '../../components/admin/RichTextEditor';
import CoverImageUploader from '../../components/admin/CoverImageUploader';
import { DOWNLOADS, downloadAll, triggerSvgDownload } from '../../utils/qmgAssets';

const emptyForm = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  cover_image: '',
  tags: '',
  published: false,
  scheduled_at_local: '', // datetime-local value e.g. 2026-05-09T08:30
};

const generateSlug = (title) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localInputToIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const formatDateTime = (iso) =>
  new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const getPostStatus = (post) => {
  if (!post.published) return 'draft';
  if (post.scheduled_at && new Date(post.scheduled_at) > new Date()) return 'scheduled';
  return 'published';
};

const STATUS_BADGE = {
  draft: { label: 'Draft', className: 'bg-[#F2F0E9] text-[#737373]' },
  scheduled: { label: 'Scheduled', className: 'bg-[#BC562B]/10 text-[#BC562B]' },
  published: { label: 'Published', className: 'bg-[#2C5530]/10 text-[#2C5530]' },
};

const BlogManagementPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [showAssets, setShowAssets] = useState(false);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const response = await API.get('/admin/blog');
      setPosts(response.data);
    } catch (e) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingPost(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content || '',
      cover_image: post.cover_image || '',
      tags: (post.tags || []).join(', '),
      published: post.published,
      scheduled_at_local: isoToLocalInput(post.scheduled_at),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setFormData(emptyForm);
  };

  const handleTitleChange = (title) => {
    setFormData((prev) => ({
      ...prev,
      title,
      slug: editingPost ? prev.slug : generateSlug(title),
    }));
  };

  const buildPayload = (overrides = {}) => {
    const merged = { ...formData, ...overrides };
    return {
      title: merged.title,
      slug: merged.slug,
      excerpt: merged.excerpt,
      content: merged.content,
      cover_image: merged.cover_image || null,
      tags: merged.tags
        ? merged.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [],
      published: !!merged.published,
      scheduled_at: localInputToIso(merged.scheduled_at_local),
    };
  };

  const savePost = async (payload, successMsg) => {
    setSaving(true);
    try {
      if (editingPost) {
        await API.put(`/admin/blog/${editingPost.id}`, payload);
      } else {
        await API.post('/admin/blog', payload);
      }
      toast.success(successMsg);
      closeForm();
      await fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save blog post');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async (e) => {
    e?.preventDefault();
    if (!formData.title.trim() || !formData.slug.trim()) {
      toast.error('Title and slug are required.');
      return;
    }
    await savePost(buildPayload({ published: false }), 'Draft saved.');
  };

  const handlePublishNow = async () => {
    if (!formData.title.trim() || !formData.slug.trim() || !formData.excerpt.trim()) {
      toast.error('Title, slug and excerpt are required to publish.');
      return;
    }
    await savePost(buildPayload({ published: true, scheduled_at_local: '' }), 'Post published!');
  };

  const handleSchedule = async () => {
    if (!formData.title.trim() || !formData.slug.trim() || !formData.excerpt.trim()) {
      toast.error('Title, slug and excerpt are required to schedule.');
      return;
    }
    if (!formData.scheduled_at_local) {
      toast.error('Pick a date and time to schedule.');
      return;
    }
    if (new Date(formData.scheduled_at_local) <= new Date()) {
      toast.error('Schedule date must be in the future.');
      return;
    }
    await savePost(buildPayload({ published: true }), 'Post scheduled!');
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this blog post? This cannot be undone.')) return;
    try {
      await API.delete(`/admin/blog/${postId}`);
      toast.success('Blog post deleted.');
      fetchPosts();
    } catch (e) {
      toast.error('Failed to delete blog post');
    }
  };

  const togglePublished = async (post) => {
    try {
      await API.put(`/admin/blog/${post.id}`, {
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        content: post.content,
        cover_image: post.cover_image || null,
        tags: post.tags || [],
        published: !post.published,
        scheduled_at: post.scheduled_at || null,
      });
      toast.success(post.published ? 'Post unpublished.' : 'Post published.');
      fetchPosts();
    } catch (e) {
      toast.error('Failed to update post');
    }
  };

  const filteredPosts = useMemo(
    () =>
      posts.filter(
        (post) =>
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (post.excerpt || '').toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [posts, searchTerm],
  );

  return (
    <div data-testid="blog-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">Blog Management</h1>
          <p className="text-[#57534E]">Create rich posts, upload images, and schedule for later.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowAssets(true)}
            className="flex items-center gap-2 border border-[#2C5530] text-[#2C5530] px-5 py-3 font-semibold hover:bg-[#2C5530] hover:text-white transition-colors"
            data-testid="open-qmg-assets-btn"
          >
            <Download className="w-5 h-5" /> QMG Symbols
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors"
            data-testid="add-post-btn"
          >
            <Plus className="w-5 h-5" /> New Post
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
        <input
          type="text"
          placeholder="Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10"
          data-testid="search-posts"
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 z-50 overflow-y-auto"
          data-testid="post-form-modal"
        >
          <div className="bg-white w-full max-w-4xl my-6 p-6 md:p-8 shadow-xl" data-testid="post-form">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6">
              {editingPost ? 'Edit Blog Post' : 'New Blog Post'}
            </h2>

            <form onSubmit={handleSaveDraft} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    required
                    className="w-full"
                    placeholder="Post title"
                    data-testid="post-title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Slug (URL)</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                    required
                    className="w-full font-mono text-sm"
                    placeholder="post-url-slug"
                    data-testid="post-slug"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData((prev) => ({ ...prev, excerpt: e.target.value }))}
                  rows={2}
                  className="w-full"
                  placeholder="Brief summary shown on the blog list and in social previews"
                  data-testid="post-excerpt"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Cover Image</label>
                <CoverImageUploader
                  value={formData.cover_image}
                  onChange={(url) => setFormData((prev) => ({ ...prev, cover_image: url }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Content</label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
                />
                <p className="text-xs text-[#737373] mt-1">Use the toolbar to format text, add links, and insert images.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
                  className="w-full"
                  placeholder="mental game, focus, pre-shot routine"
                  data-testid="post-tags"
                />
              </div>

              {/* Schedule */}
              <div className="border border-[#E7E5E4] p-4 rounded-sm bg-[#FAFAF9]">
                <div className="flex items-center gap-2 text-[#1A1A1A] mb-3">
                  <Clock className="w-4 h-4 text-[#BC562B]" />
                  <span className="font-medium text-sm">Schedule for later (optional)</span>
                </div>
                <input
                  type="datetime-local"
                  value={formData.scheduled_at_local}
                  onChange={(e) => setFormData((prev) => ({ ...prev, scheduled_at_local: e.target.value }))}
                  className="w-full md:w-auto"
                  data-testid="post-schedule"
                />
                {formData.scheduled_at_local && (
                  <p className="text-xs text-[#57534E] mt-2">
                    Will go live at <strong>{formatDateTime(localInputToIso(formData.scheduled_at_local))}</strong>.
                    Use the <em>Schedule Post</em> button below to confirm.
                  </p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-[#E7E5E4]">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#2C5530] text-[#2C5530] px-6 py-3 font-semibold hover:bg-[#2C5530] hover:text-white transition-colors disabled:opacity-50"
                  data-testid="save-draft-btn"
                >
                  <FileText className="w-4 h-4" />
                  Save as Draft
                </button>
                {formData.scheduled_at_local ? (
                  <button
                    type="button"
                    onClick={handleSchedule}
                    disabled={saving}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#BC562B] text-white px-6 py-3 font-semibold hover:bg-[#9A4520] transition-colors disabled:opacity-50"
                    data-testid="schedule-post-btn"
                  >
                    <Calendar className="w-4 h-4" />
                    Schedule Post
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handlePublishNow}
                    disabled={saving}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors disabled:opacity-50"
                    data-testid="publish-now-btn"
                  >
                    <Eye className="w-4 h-4" />
                    Publish Now
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-6 py-3 border border-[#E7E5E4] text-[#57534E] hover:bg-[#F2F0E9] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Posts List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-20 bg-white border border-[#E7E5E4]">
          <FileText className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
          <p className="text-[#737373]">{searchTerm ? 'No posts match your search' : 'No blog posts yet'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map((post, index) => {
            const status = getPostStatus(post);
            const badge = STATUS_BADGE[status];
            return (
              <div
                key={post.id}
                className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530]/30 transition-colors"
                data-testid={`post-${index}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-semibold text-[#1A1A1A] text-lg">{post.title}</h3>
                      <span className={`text-xs px-2 py-1 ${badge.className}`}>{badge.label}</span>
                      {status === 'scheduled' && post.scheduled_at && (
                        <span className="text-xs text-[#BC562B] flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(post.scheduled_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-[#57534E] text-sm mb-3 line-clamp-2">{post.excerpt}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#737373]">
                      <span>/{post.slug}</span>
                      <span>{formatDate(post.created_at)}</span>
                      {post.tags?.length > 0 && (
                        <div className="flex gap-1">
                          {post.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="bg-[#F2F0E9] px-2 py-0.5 text-xs">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => togglePublished(post)}
                      className={`p-2 ${post.published ? 'text-[#2C5530]' : 'text-[#737373]'} hover:bg-[#F2F0E9] transition-colors`}
                      title={post.published ? 'Unpublish' : 'Publish'}
                      data-testid={`toggle-publish-${index}`}
                    >
                      {post.published ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => openEdit(post)}
                      className="p-2 text-[#57534E] hover:bg-[#F2F0E9] transition-colors"
                      title="Edit"
                      data-testid={`edit-post-${index}`}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-2 text-[#BC562B] hover:bg-red-50 transition-colors"
                      title="Delete"
                      data-testid={`delete-post-${index}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* QMG Symbols Download Modal */}
      {showAssets && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
          onClick={() => setShowAssets(false)}
          data-testid="qmg-assets-modal"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative bg-white w-full max-w-2xl my-6 p-6 md:p-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAssets(false)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full text-[#57534E] hover:bg-[#F2F0E9] hover:text-[#1A1A1A] flex items-center justify-center transition-colors"
              aria-label="Close"
              data-testid="close-qmg-assets-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">QMG Blueprint Symbols</h2>
            <p className="text-sm text-[#57534E] mb-6 max-w-lg">
              Download the official Awareness · Acceptance · Action symbols as <strong>SVG</strong> files — scalable, print-ready, and editable in any design tool. Use them anywhere off-platform.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {DOWNLOADS.map((f) => (
                <button
                  key={f.name}
                  onClick={() => { triggerSvgDownload(f.name, f.svg); toast.success(`${f.name} downloaded.`); }}
                  className="group flex flex-col items-center gap-2 p-4 border border-[#E7E5E4] hover:border-[#2C5530] hover:shadow-sm transition-all text-center"
                  data-testid={`download-${f.name}`}
                >
                  <div
                    className="w-full max-w-[120px] aspect-square flex items-center justify-center"
                    dangerouslySetInnerHTML={{ __html: f.svg }}
                  />
                  <span className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-wider flex items-center gap-1">
                    <Download className="w-3 h-3" />
                    {f.name.replace('qmg-', '').replace('.svg', '').replace(/-/g, ' ')}
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={() => { downloadAll(); toast.success('All 4 SVG files downloading.'); }}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors"
              data-testid="download-all-btn"
            >
              <Download className="w-4 h-4" />
              Download all 4 SVGs
            </button>

            <div className="mt-6 p-4 bg-[#FAFAF9] border border-[#E7E5E4] text-sm text-[#57534E]">
              <p className="font-semibold text-[#1A1A1A] mb-1">Want to drop the symbols inside a blog post?</p>
              <p>Open <em>New Post</em> (or edit one) and in the rich-text toolbar click the <strong>QMG</strong> button — the full Awareness · Acceptance · Action block is inserted at the cursor.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagementPage;
